import { useEffect, useCallback } from "react";
import { useRecoilValue } from "recoil";
import { insetState } from "../../../recoil/inset";
import useGeoLocation from "../../../hooks/useGeoLocation";
import useDialog from "../../../hooks/useDialog";
import LocationSheet from "../../../components/web/LocationSheet";

// 컴포넌트 imports
import FranchiseHeaderComponent from "./components/franchise-header";
import FranchiseAreaFilter from "./components/franchise-area-filter";
import { FranchiseItem } from "./components/franchise-item";
import FranchiseItemSkeleton from "./components/franchise-item/skeleton";
import FranchiseMore from "./components/franchise-more";

// 스타일 imports
import {
  FranchisePubsListWrapper,
  FranchisePubsWrapper,
  RunnerWrapper,
  FranchisePubsEmptyWrapper,
} from "./styles";

// 커스텀 훅 imports
import {
  useFranchisePubData,
  useFranchiseFilters,
  useFranchiseModal,
} from "./hooks";

const FranchisePubs = () => {
  // Recoil 상태
  const inset = useRecoilValue(insetState);

  // 위치 정보
  const { latitude, longitude, updateLocation } = useGeoLocation();

  // 다이얼로그 훅
  const { openDialog } = useDialog();

  // 커스텀 훅들
  const { allList, loading, error, fetchPubList } = useFranchisePubData(
    latitude,
    longitude
  );

  const {
    search,
    selectedArea,
    isMyPub,
    setSearch,
    setSelectedArea,
    setIsMyPub,
    handleSearch,
    filteredList,
    hasActiveFilters,
    filterCount,
  } = useFranchiseFilters(allList);

  const { showMoreOption, selectedStore, openMoreOption, closeMoreOption } =
    useFranchiseModal();

  // 내 위치 업데이트
  const updateMyLocation = useCallback(async () => {
    await updateLocation();
  }, [updateLocation]);

  // 초기 위치 설정 및 iOS 위치 권한 체크
  useEffect(() => {
    updateMyLocation();
    if (latitude === 37.493743896484375 && longitude === 127.0637597) {
      openDialog({
        title: "정확한 위치(ios)",
        type: "web",
        text: "더욱 정확한 위치 정보를 얻기 위해<br>아래와 같이 설정하세요.<br><br>설정 -> 러너러너 -> 위치 -> 정확한 위치 허용",
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        },
      });
    }
  }, [updateMyLocation, latitude, longitude, openDialog]);

  // 위치 변경 시 데이터 페칭
  useEffect(() => {
    fetchPubList();
  }, [fetchPubList]);

  // FranchiseItem에서 사용할 핸들러들
  const handleMoreOption = useCallback(
    (cafe: any) => {
      openMoreOption(cafe);
    },
    [openMoreOption]
  );

  return (
    <FranchisePubsWrapper isApp={inset}>
      <FranchiseHeaderComponent
        search={search}
        setSearch={setSearch}
        onSearch={handleSearch}
      />
      <LocationSheet />
      {showMoreOption && selectedStore && (
        <FranchiseMore
          cafe={selectedStore}
          setShowMoreOption={() => closeMoreOption()}
          setSelectedStore={() => closeMoreOption()}
          showMoreOption={showMoreOption}
        />
      )}
      <FranchiseAreaFilter
        isMyPub={isMyPub}
        setIsMyPub={setIsMyPub}
        setArea={setSelectedArea}
        selectedArea={selectedArea}
      />

      <RunnerWrapper>
        <FranchisePubsListWrapper>
          {loading && (
            <>
              {Array.from({ length: 10 }, (_, index) => (
                <FranchiseItemSkeleton key={`skeleton-${index}`} />
              ))}
            </>
          )}

          {error && (
            <FranchisePubsEmptyWrapper color="red">
              {error}
            </FranchisePubsEmptyWrapper>
          )}

          {!loading && !error && filteredList.length === 0 && (
            <FranchisePubsEmptyWrapper>
              {hasActiveFilters
                ? `검색 결과가 없습니다. (필터 ${filterCount}개 적용 중)`
                : "홀덤펍이 없습니다."}
            </FranchisePubsEmptyWrapper>
          )}

          {!loading &&
            filteredList.map((item, index) => (
              <FranchiseItem
                item={item}
                key={`${item.id}-${index}`}
                setSelectedStore={handleMoreOption}
                setShowMoreOption={() => handleMoreOption(item)}
              />
            ))}
        </FranchisePubsListWrapper>
      </RunnerWrapper>
    </FranchisePubsWrapper>
  );
};

export default FranchisePubs;
